import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useGoToStripeExpressSignIn } from "@src/appV2/redesign/Accounts/PayoutSettings/hooks/useGoToStripeExpressSignIn";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";

import {
  getWorkerStripeStatusQueryKey,
  useGetWorkerStripeStatus,
} from "../api/useGetWorkerStripeStatus";
import { StripeRestrictionBanner } from "./StripeRestrictionBanner";

interface StripeRestrictionBannerContainerProps {
  sourceUI: "shifts-tab" | "account-tab";
}

export function StripeRestrictionBannerContainer(props: StripeRestrictionBannerContainerProps) {
  const { sourceUI } = props;

  const worker = useDefinedWorker();

  const isPaymentAccountInfoDefined = isDefined(worker.paymentAccountInfo);
  // Onboarding is completed if it's undefined or is true
  const isOnboardingCompleted =
    isPaymentAccountInfoDefined &&
    (!isDefined(worker.paymentAccountInfo?.isOnboardingCompleted) ||
      worker.paymentAccountInfo.isOnboardingCompleted);

  const isBannerFeatureFlagEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_STRIPE_RESTRICTION_BANNER, {
    defaultValue: false,
  });

  const shouldFetchStripeStatus =
    isBannerFeatureFlagEnabled && isOnboardingCompleted && isPaymentAccountInfoDefined;

  const queryClient = useQueryClient();

  const { goToStripeExpressSignIn, isGoToStripeExpressSignInLoading } =
    useGoToStripeExpressSignIn();

  const { data: stripeStatus } = useGetWorkerStripeStatus({
    enabled: shouldFetchStripeStatus,
  });

  const { restrictionStage, restrictionMessage, restrictionDeadlineInSeconds } =
    stripeStatus?.meta ?? {};
  const areDetailsSubmitted = stripeStatus?.data.attributes.areDetailsSubmitted;
  const disabledReason = stripeStatus?.data.attributes.requirements.disabledReason;

  const isBannerShown =
    shouldFetchStripeStatus &&
    areDetailsSubmitted === true &&
    isDefined(restrictionStage) &&
    restrictionStage !== "info" &&
    isDefined(restrictionMessage);

  useLogEffect(
    APP_V2_USER_EVENTS.STRIPE_RESTRICTION_BANNER_SHOWN,
    {
      sourceUI,
      restrictionStage,
      restrictionMessage,
      disabledReason,
    },
    { enabled: isBannerShown }
  );

  if (!isBannerShown) {
    return null;
  }

  return (
    <Box>
      <StripeRestrictionBanner
        stage={restrictionStage}
        message={restrictionMessage}
        deadlineInSeconds={restrictionDeadlineInSeconds}
        isLoading={isGoToStripeExpressSignInLoading}
        onClick={() => {
          logEvent(APP_V2_USER_EVENTS.STRIPE_RESTRICTION_BANNER_CTA_CLICKED, {
            sourceUI,
            restrictionStage,
            restrictionMessage,
            disabledReason,
          });
          void queryClient.invalidateQueries({
            queryKey: getWorkerStripeStatusQueryKey(worker.userId),
            refetchType: "none",
          });
          goToStripeExpressSignIn();
        }}
      />
    </Box>
  );
}
